import { createApi } from '@reduxjs/toolkit/query/react';
import { brandApi } from 'api/brand';
import { clientApi } from 'api/client';
import baseQueryWithTokenManagement from 'pages/auth/apiSlice';
import { globalApi } from 'api/global';
import { supplierApi } from 'api/supplier';
import { taskApi } from './task';
import { communicationHubApi } from 'api/communicationHub';
import { leadsApi } from './leads';
import { itemApi } from 'api/item';
import { purchaseRequestApi } from 'api/purchaseRequest';
import { supplierOfferApi } from 'api/supplierOffer';
import { warehousePackagesApi } from 'api/warehouse/packages';

export const filesApi = createApi({
  tagTypes: ['CLIENT_FILES', 'BRAND_FILES', 'PRICE_LIST_FILES', 'SUPPLIER_FILES'],
  reducerPath: 'filesApi',
  baseQuery: baseQueryWithTokenManagement,
  endpoints: (builder) => ({
    clientFileCreate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/fss/api/client',
        method: 'post',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(clientApi.util.invalidateTags(['CLIENT']));
      },
      invalidatesTags: () => ['CLIENT_FILES'],
    }),
    clientFile: builder.query<any, { clientId: string; fileName: string }>({
      query: ({ clientId, fileName }) => ({
        url: '/fss/api/client',
        params: { clientId, fileName }, // Use query params, not the body, for GET requests
      }),
      transformResponse: (response: any) => {
        return response;
      },
      providesTags: ['CLIENT_FILES'], // Provide tags so that the cache can be managed
    }),
    clientFileDelete: builder.mutation<any, any>({
      query: (data) => ({
        url: '/fss/api/client',
        method: 'delete',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(clientApi.util.invalidateTags(['CLIENT']));
      },
      invalidatesTags: () => ['CLIENT_FILES'],
    }),
    brandFileCreate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/fss/api/brand',
        method: 'post',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(brandApi.util.invalidateTags(['BRAND', 'BRAND_LIST']));
      },
      invalidatesTags: () => ['BRAND_FILES'],
    }),
    brandFile: builder.query<any, { brandtId: string; fileName: string }>({
      query: ({ brandtId, fileName }) => ({
        url: '/fss/api/brand',
        params: { brandtId, fileName },
      }),
      transformResponse: (response: any) => {
        return response;
      },
      providesTags: ['BRAND_FILES'],
    }),
    brandFileDelete: builder.mutation<any, any>({
      query: (data) => ({
        url: '/fss/api/brand',
        method: 'delete',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(brandApi.util.invalidateTags(['BRAND', 'BRAND_LIST']));
      },
      invalidatesTags: () => ['BRAND_FILES'],
    }),
    // TODO need to implement file deletion from fss
    priceListFile: builder.mutation<any, any>({
      query: (data) => ({
        url: '/fss/api/price-list',
        method: 'post',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(supplierApi.util.invalidateTags(['SUPPLIER_PRICE_LIST', 'SUPPLIER', 'SUPPLIER_LIST']));
        dispatch(brandApi.util.invalidateTags(['PRICE_LIST', 'BRAND', 'BRAND_LIST']));
      },
      invalidatesTags: () => ['PRICE_LIST_FILES'],
    }),
    attachmentFileCreate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/fss/api/attachments',
        method: 'post',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(globalApi.util.invalidateTags(['ATTACHMENT_LIST']));
        dispatch(purchaseRequestApi.util.invalidateTags(['PURCHASE_REQUEST', 'PURCHASE_REQUEST_LIST']));
        dispatch(supplierOfferApi.util.invalidateTags(['SUPPLIER_OFFER_LIST', 'SUPPLIER_OFFER']));
      },
    }),
    attachmentFileDelete: builder.mutation<any, any>({
      query: (data) => ({
        url: '/fss/api/attachments',
        method: 'delete',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(globalApi.util.invalidateTags(['ATTACHMENT_LIST']));
        dispatch(purchaseRequestApi.util.invalidateTags(['PURCHASE_REQUEST', 'PURCHASE_REQUEST_LIST']));
        dispatch(supplierOfferApi.util.invalidateTags(['SUPPLIER_OFFER_LIST', 'SUPPLIER_OFFER']));
      },
    }),
    // TODO same for brand priclist create
    supplierFileCreate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/fss/api/supplier',
        method: 'post',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(supplierApi.util.invalidateTags(['SUPPLIER_PRICE_LIST', 'SUPPLIER']));
      },
      invalidatesTags: () => ['SUPPLIER_FILES'],
    }),
    supplierFileDelete: builder.mutation<any, any>({
      query: (data) => ({
        url: '/fss/api/supplier',
        method: 'delete',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(supplierApi.util.invalidateTags(['SUPPLIER_PRICE_LIST', 'SUPPLIER']));
      },
      invalidatesTags: () => ['SUPPLIER_FILES'],
    }),
    attachmentFileBulkCreate: builder.mutation<any, any>({
      query: ({ data }) => ({
        url: '/fss/api/attachments/bulk',
        method: 'post',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        const entity: Record<string, any> = {
          COMMUNICATION_HUB: {
            api: communicationHubApi,
            tags: ['NOTE_LIST', 'PINNED_NOTE_LIST'],
          },
          TASK: {
            api: taskApi,
            tags: ['TASK_LIST'],
          },
          ITEM: {
            api: itemApi,
            tags: ['ITEM_LIST'],
          },
          LEAD: {
            api: leadsApi,
            tags: ['LEAD', 'LEAD_LIST'],
          },
          PURCHASE_REQUEST: {
            api: purchaseRequestApi,
            tags: ['PURCHASE_REQUEST_LIST', 'PURCHASE_REQUEST', 'PURCHASE_REQUEST_SUPPLIER_OFFER'],
          },
          PURCHASE_ORDER: {
            api: warehousePackagesApi,
            tags: ['INBOUND_PACKAGES', 'INBOUND_PACKAGE_DETAILS'],
          },
          PURCHASE_ORDER_ITEM: {
            api: warehousePackagesApi,
            tags: ['INBOUND_PACKAGES', 'INBOUND_PACKAGE_DETAILS'],
          },
        };
        dispatch(entity[arg.entityName].api.util.invalidateTags(entity[arg.entityName].tags));
      },
    }),
  }),
});

export const {
  useClientFileCreateMutation,
  useClientFileQuery,
  useBrandFileQuery,
  useBrandFileCreateMutation,
  useBrandFileDeleteMutation,
  useClientFileDeleteMutation,
  usePriceListFileMutation,
  useAttachmentFileCreateMutation,
  useAttachmentFileDeleteMutation,
  useSupplierFileCreateMutation,
  useSupplierFileDeleteMutation,
  useAttachmentFileBulkCreateMutation,
} = filesApi;
